'use client';;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:tillamook-com":true});}catch(e){}}();

import React, { useEffect, useState } from 'react';

import cx from 'clsx';
import PropTypes from 'prop-types';

import { BLOCKS } from '@contentful/rich-text-types';

import Button from 'components/ui/Button';
import ButtonCTA from 'components/ui/ButtonCTA';
import Icon from 'components/ui/Icon';
import RichText from 'components/ui/RichText';
import Text from 'components/ui/Text';

import styles from './PairingWheelContent.module.scss';

const headlineOverrides = {
    renderNode: {
        [BLOCKS.PARAGRAPH]: (node, children) => {
            return (
                <Text
                    as="p"
                    baseTheme="displayXSmall"
                    themes={{
                        large: 'displaySmall',
                        xxLarge: 'displayMedium',
                    }}
                >
                    {children}
                </Text>
            );
        },
    },
};

const introCopyOverrides = {
    renderNode: {
        [BLOCKS.PARAGRAPH]: (node, children) => {
            return (
                <Text
                    as="p"
                    className={styles.paragraph}
                    baseTheme="bodySmall"
                    themes={{
                        large: 'bodyMedium',
                        xxLarge: 'bodyLarge',
                    }}
                >
                    {children}
                </Text>
            );
        },
        [BLOCKS.HEADING_3]: (node, children) => {
            return (
                <Text
                    as="h3"
                    className={styles.label}
                    baseTheme="labelLargeAlt"
                >
                    {children}
                </Text>
            );
        },
    },
};

const copyOverrides = {
    renderNode: {
        [BLOCKS.PARAGRAPH]: (node, children) => {
            return (
                <Text
                    as="p"
                    className={styles.paragraph}
                    baseTheme="bodyXSmall"
                    themes={{
                        large: 'bodySmall',
                        xxLarge: 'bodyMedium',
                    }}
                >
                    {children}
                </Text>
            );
        },
        [BLOCKS.HEADING_3]: (node, children) => {
            return (
                <Text
                    as="h3"
                    className={styles.label}
                    baseTheme="labelLargeAlt"
                >
                    {children}
                </Text>
            );
        },
    },
};

const loadingOverrides = {
    renderNode: {
        [BLOCKS.PARAGRAPH]: (node, children) => {
            return (
                <Text
                    as="p"
                    baseTheme="displaySmall"
                    themes={{
                        large: 'displayMedium',
                        xxLarge: 'displayLarge',
                    }}
                >
                    {children}
                </Text>
            );
        },
    },
};

const PairingWheelContent = ({
    eyebrow,
    headline,
    copy,
    items,
    handleSpinClick,
    handleRevealClick,
    uniqueSpins,
    isSpinsComplete,
    currentContentSection,
    loadingIcon,
    loadingMessage1,
    loadingMessage2,
    loadingMessage3,
    loadingMessage4,
}) => {
    const [isSpinning, setIsSpinning] = useState(false);

    const defaultMessage = {
        json: {
            content: [
                {
                    data: {},
                    nodeType: 'paragraph',
                    content: [
                        {
                            nodeType: 'text',
                            value: 'Almost',
                            marks: [],
                            data: {},
                        },
                    ],
                },
                {
                    data: {},
                    nodeType: 'paragraph',
                    content: [
                        {
                            nodeType: 'text',
                            value: 'Ready…',
                            marks: [],
                            data: {},
                        },
                    ],
                },
            ],
        },
    };
    const [message, setMessage] = useState(loadingMessage1 || defaultMessage);

    const messages = React.useMemo(() => {
        // Filter out any undefined, null, or empty messages
        const validMessages = [
            loadingMessage1,
            loadingMessage2,
            loadingMessage3,
            loadingMessage4,
        ].filter(msg => msg && Object.keys(msg).length > 0);

        return validMessages;
    }, [loadingMessage1, loadingMessage2, loadingMessage3, loadingMessage4]);

    const hasCurrentSection =
        currentContentSection !== null && currentContentSection !== undefined;

    useEffect(() => {
        let timer;

        if (
            currentContentSection !== null &&
            currentContentSection !== undefined
        ) {
            // Only set a random message if we have valid messages
            if (messages.length > 0) {
                const randomIndex = Math.floor(Math.random() * messages.length);
                const selectedMessage = messages[randomIndex];
                setMessage(selectedMessage);
            }

            setIsSpinning(true);

            timer = setTimeout(() => {
                setIsSpinning(false);
            }, 2800);
        }

        return () => clearTimeout(timer);
    }, [currentContentSection, messages]);

    return (
        <div
            className={cx(styles.root, {
                [styles.isInitial]: !hasCurrentSection,
                [styles.isItemsActive]: hasCurrentSection && !isSpinning,
                [styles.isSpinsComplete]: isSpinsComplete,
                [styles.isSpinning]: isSpinning,
            })}
        >
            <div className={cx(styles.contentPanel, styles.initial)}>
                <Text
                    className={styles.eyebrow}
                    baseTheme="labelSmall"
                    themes={{
                        large: 'labelLarge',
                    }}
                >
                    {eyebrow}
                </Text>
                {headline && (
                    <div className={styles.headline}>
                        <RichText
                            richText={headline}
                            overrides={headlineOverrides}
                        />
                    </div>
                )}
                {copy && (
                    <div className={styles.copy}>
                        <RichText
                            richText={copy}
                            overrides={introCopyOverrides}
                        />
                    </div>
                )}
                <div className={styles.buttons}>
                    <ButtonCTA
                        className={styles.cta}
                        text="Spin To Pair"
                        onClick={handleSpinClick}
                        style="filled-blue-cream"
                        tabIndex={hasCurrentSection ? 0 : -1}
                        gtm-label="spin-to-pair"
                    />
                </div>
            </div>
            {items.map((item, index) => {
                return (
                    <div
                        key={index}
                        className={cx(styles.contentPanel, styles.item, {
                            [styles.isActive]: currentContentSection === index,
                        })}
                    >
                        <div className={styles.contentShift}>
                            <Text
                                className={styles.eyebrow}
                                baseTheme="labelSmall"
                                themes={{
                                    large: 'labelLarge',
                                }}
                            >
                                {item.eyebrow}
                            </Text>
                            {item.headline && (
                                <div className={styles.headline}>
                                    <RichText
                                        richText={item.headline}
                                        overrides={headlineOverrides}
                                    />
                                </div>
                            )}
                            {item.copy && (
                                <div className={styles.copy}>
                                    <RichText
                                        richText={item.copy}
                                        overrides={copyOverrides}
                                    />
                                </div>
                            )}
                        </div>
                        <div className={styles.buttons}>
                            <ButtonCTA
                                className={styles.cta}
                                text="Spin"
                                onClick={handleSpinClick}
                                style="filled-blue-cream"
                                tabIndex={hasCurrentSection ? 0 : -1}
                                gtm-label="spin"
                            />
                            <div className={styles.progress}>
                                <div className={styles.progressContent}>
                                    <div className={styles.progressText}>
                                        {uniqueSpins} / 8
                                    </div>
                                    <div className={styles.progressBar}>
                                        <div
                                            className={styles.progressBarFill}
                                            style={{
                                                transform: `translateX(${
                                                    (uniqueSpins / 8) * 100 -
                                                    100
                                                }%)`,
                                            }}
                                        ></div>
                                    </div>
                                </div>
                                <Button
                                    className={styles.revealContent}
                                    tabIndex={isSpinsComplete ? 0 : -1}
                                    onClick={handleRevealClick}
                                    gtm-label="reveal-content"
                                >
                                    <span className={styles.revealContentText}>
                                        Reveal
                                        <span
                                            className={
                                                styles.revealContentSurprise
                                            }
                                        >
                                            {' '}
                                            Surprise
                                        </span>
                                    </span>
                                </Button>
                                <div className={styles.revealButtonIcon}>
                                    <svg
                                        width="100%"
                                        height="100%"
                                        viewBox="0 0 17 16"
                                        fill="none"
                                    >
                                        <path
                                            d="M4.67188 0.71581C5.02505 -0.238602 6.37495 -0.238604 6.72812 0.715808L7.48664 2.76568C7.59767 3.06575 7.83425 3.30233 8.13432 3.41336L10.1842 4.17188C11.1386 4.52505 11.1386 5.87495 10.1842 6.22812L8.13432 6.98664C7.83425 7.09767 7.59767 7.33425 7.48664 7.63432L6.72812 9.68419C6.37495 10.6386 5.02505 10.6386 4.67188 9.68419L3.91336 7.63432C3.80233 7.33425 3.56575 7.09767 3.26568 6.98664L1.21581 6.22812C0.261398 5.87495 0.261396 4.52505 1.21581 4.17188L3.26568 3.41336C3.56575 3.30233 3.80233 3.06575 3.91336 2.76568L4.67188 0.71581Z"
                                            fill="#001E60"
                                        />
                                        <path
                                            d="M12.188 9.29537C12.4325 8.63462 13.3671 8.63462 13.6116 9.29536L14.1367 10.7145C14.2136 10.9222 14.3774 11.086 14.5851 11.1629L16.0042 11.688C16.665 11.9325 16.665 12.8671 16.0042 13.1116L14.5851 13.6367C14.3774 13.7136 14.2136 13.8774 14.1367 14.0851L13.6116 15.5042C13.3671 16.165 12.4325 16.165 12.188 15.5042L11.6629 14.0851C11.586 13.8774 11.4222 13.7136 11.2145 13.6367L9.79537 13.1116C9.13462 12.8671 9.13462 11.9325 9.79536 11.688L11.2145 11.1629C11.4222 11.086 11.586 10.9222 11.6629 10.7145L12.188 9.29537Z"
                                            fill="#001E60"
                                        />
                                    </svg>
                                </div>
                            </div>
                        </div>
                    </div>
                );
            })}
            <div className={cx(styles.contentPanel, styles.spinning)}>
                <div className={styles.spinningIcon}>
                    <Icon
                        className={styles.icon}
                        src={loadingIcon?.icon?.url}
                    />
                </div>
                {message && (
                    <div className={styles.spinningHeadline}>
                        <RichText
                            richText={message}
                            overrides={loadingOverrides}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

PairingWheelContent.propTypes = {
    eyebrow: PropTypes.string,
    headline: PropTypes.object,
    copy: PropTypes.object,
    items: PropTypes.arrayOf(
        PropTypes.shape({
            eyebrow: PropTypes.string,
            headline: PropTypes.object,
            copy: PropTypes.object,
        })
    ),
    handleSpinClick: PropTypes.func.isRequired,
    handleRevealClick: PropTypes.func.isRequired,
    uniqueSpins: PropTypes.number.isRequired,
    isSpinsComplete: PropTypes.bool.isRequired,
    currentContentSection: PropTypes.number,
};

export default PairingWheelContent;
